// WebGL2 post-processing pass that runs the "Serenity" CRT shader
// (https://github.com/gingerbeardman/webgl-crt-shader, MIT) over a 2D canvas.
// Adapted from the repo's crt-webgl.js: the scene canvas is uploaded as a
// texture every frame and drawn through the fragment shader.

import { CRTShader } from './CRTShader.js';

export const CRT_DEFAULTS = {
  enabled: true,
  smoothing: true,
  scanlineIntensity: 0.5,
  scanlineCount: 400,
  adaptiveIntensity: 0.3,
  brightness: 1.25,
  contrast: 1.05,
  saturation: 1.1,
  bloomIntensity: 0.45,
  bloomThreshold: 0.45,
  rgbShift: 0.35,
  vignetteStrength: 0.4,
  curvature: 0.08,
  flickerStrength: 0.015
};

const UNIFORM_NAMES = [
  'uTexture',
  'uEnabled',
  'scanlineIntensity',
  'scanlineCount',
  'time',
  'yOffset',
  'brightness',
  'contrast',
  'saturation',
  'bloomIntensity',
  'bloomThreshold',
  'rgbShift',
  'adaptiveIntensity',
  'vignetteStrength',
  'curvature',
  'flickerStrength'
];

const VERTEX_SOURCE = `#version 300 es
precision highp float;
const vec2 pos[4] = vec2[4](vec2(-1.0, -1.0), vec2(1.0, -1.0), vec2(-1.0, 1.0), vec2(1.0, 1.0));
const vec2 uvData[4] = vec2[4](vec2(0.0, 0.0), vec2(1.0, 0.0), vec2(0.0, 1.0), vec2(1.0, 1.0));
out vec2 vUv;
void main() {
  vUv = uvData[gl_VertexID];
  gl_Position = vec4(pos[gl_VertexID], 0.0, 1.0);
}`;

function buildFragmentSource() {
  let body = CRTShader.fragmentShader;
  body = body.replace(/#ifdef GL_FRAGMENT_PRECISION_HIGH[\s\S]*?#endif/, '');
  body = body.replace(/uniform\s+[^;]+;\s*/g, '');
  body = body.replace(/varying\s+[^;]+;\s*/g, '');
  body = body.replace(/\btDiffuse\b/g, 'uTexture');
  body = body.replace(/texture2D\s*\(/g, 'texture(');
  body = body.replace(/gl_FragColor/g, 'fragColor');
  body = body.replace(/void main\s*\(/, 'void mainImage(');
  return `#version 300 es
precision highp float;
uniform sampler2D uTexture;
uniform bool uEnabled;
uniform float scanlineIntensity;
uniform float scanlineCount;
uniform float time;
uniform float yOffset;
uniform float brightness;
uniform float contrast;
uniform float saturation;
uniform float bloomIntensity;
uniform float bloomThreshold;
uniform float rgbShift;
uniform float adaptiveIntensity;
uniform float vignetteStrength;
uniform float curvature;
uniform float flickerStrength;
in vec2 vUv;
out vec4 fragColor;
${body}
void main() {
  if (!uEnabled) { fragColor = texture(uTexture, vUv); return; }
  mainImage();
}`;
}

function compileShader(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    throw new Error(gl.getShaderInfoLog(shader) || 'Shader compile error');
  }
  return shader;
}

function createProgram(gl, vsSource, fsSource) {
  const program = gl.createProgram();
  gl.attachShader(program, compileShader(gl, gl.VERTEX_SHADER, vsSource));
  gl.attachShader(program, compileShader(gl, gl.FRAGMENT_SHADER, fsSource));
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error(gl.getProgramInfoLog(program) || 'Program link error');
  }
  return program;
}

export class CRTRenderer {
  constructor(canvas, source, params = {}) {
    this.canvas = canvas;
    this.source = source;
    this.params = { ...CRT_DEFAULTS, ...params };

    const gl = canvas.getContext('webgl2', { alpha: false, antialias: false, depth: false, stencil: false });
    if (!gl) throw new Error('WebGL2 not supported');
    this.gl = gl;

    this.program = createProgram(gl, VERTEX_SOURCE, buildFragmentSource());
    this.vao = gl.createVertexArray();
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);

    this.u = {};
    for (const name of UNIFORM_NAMES) this.u[name] = gl.getUniformLocation(this.program, name);

    this.texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.setSmoothing(this.params.smoothing);
    this.allocate();
  }

  // Call after the source canvas changes size.
  allocate() {
    const { gl, source, canvas } = this;
    canvas.width = source.width;
    canvas.height = source.height;
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, source.width, source.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  }

  setSmoothing(enabled) {
    const { gl } = this;
    this.params.smoothing = enabled;
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    const filter = enabled ? gl.LINEAR : gl.NEAREST;
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
  }

  render(timeSeconds) {
    const { gl, u, params: p } = this;
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, gl.RGBA, gl.UNSIGNED_BYTE, this.source);

    gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    gl.useProgram(this.program);
    gl.bindVertexArray(this.vao);
    gl.activeTexture(gl.TEXTURE0);

    gl.uniform1i(u.uTexture, 0);
    gl.uniform1i(u.uEnabled, p.enabled ? 1 : 0);
    gl.uniform1f(u.scanlineIntensity, p.scanlineIntensity);
    gl.uniform1f(u.scanlineCount, p.scanlineCount);
    gl.uniform1f(u.time, timeSeconds);
    gl.uniform1f(u.yOffset, 0.0);
    gl.uniform1f(u.brightness, p.brightness);
    gl.uniform1f(u.contrast, p.contrast);
    gl.uniform1f(u.saturation, p.saturation);
    gl.uniform1f(u.bloomIntensity, p.bloomIntensity);
    gl.uniform1f(u.bloomThreshold, p.bloomThreshold);
    gl.uniform1f(u.rgbShift, p.rgbShift);
    gl.uniform1f(u.adaptiveIntensity, p.adaptiveIntensity);
    gl.uniform1f(u.vignetteStrength, p.vignetteStrength);
    gl.uniform1f(u.curvature, p.curvature);
    gl.uniform1f(u.flickerStrength, p.flickerStrength);

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }
}
